import { useState, useRef } from "react";
import { Link, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "../App";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import {
  Leaf, Camera, Upload, Loader2, ArrowLeft, Sparkles,
  Sun, Droplets, Check, LogIn
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PlantIdentifier = () => {
  const { user, loading: authLoading, login } = useAuth();
  const [image, setImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const fileInputRef = useRef(null);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        toast.error("Please select an image file");
        return;
      }

      const reader = new FileReader();
      reader.onloadend = () => {
        const base64 = reader.result.split(',')[1];
        setImage(base64);
        setImagePreview(reader.result);
        setResult(null);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleIdentify = async () => {
    if (!image) {
      toast.error("Please select an image first");
      return;
    }

    setLoading(true);
    setResult(null);

    try {
      const response = await axios.post(
        `${API}/identify-plant`,
        { image_base64: image },
        { withCredentials: true }
      );

      setResult(response.data);
      toast.success("Plant identified!");
    } catch (error) {
      console.error("Identification error:", error);
      if (error.response?.status === 403) {
        toast.error("Monthly limit reached. Upgrade for unlimited identifications!");
      } else {
        toast.error("Failed to identify plant. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  const getRemainingIds = () => {
    if (user?.subscription_tier !== "free") return "Unlimited";
    const used = user?.plant_identifications_this_month || 0;
    return `${5 - used} remaining this month`;
  };

  // Show loading state while checking auth
  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="spinner" />
      </div>
    );
  }

  // Show login prompt for non-authenticated users
  if (!user) {
    return (
      <div className="min-h-screen bg-background">
        {/* Header */}
        <header className="sticky top-0 z-40 glass border-b border-border/50">
          <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Link 
                to="/"
                className="text-muted-foreground hover:text-primary transition-colors"
              >
                <ArrowLeft className="w-5 h-5" />
              </Link>
              <h1 className="font-serif text-xl font-bold text-foreground">
                Plant Identifier
              </h1>
            </div>
            <Link to="/" className="flex items-center gap-2">
              <img src="/logo.png" alt="RootSpace.Pro" className="h-8 w-auto" />
              
            </Link>
          </div>
        </header>

        <main className="max-w-2xl mx-auto px-6 py-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center"
          >
            <Card className="card-organic">
              <CardContent className="py-12">
                <Camera className="w-16 h-16 mx-auto mb-6 text-primary opacity-80" />
                <h2 className="font-serif text-2xl font-bold text-foreground mb-4">
                  Sign in to Identify Plants
                </h2>
                <p className="text-muted-foreground mb-8 max-w-md mx-auto">
                  Upload a photo and our smart plant identifier will tell you what it is, 
                  whether it&apos;s edible, and how to care for it.
                </p>
                
                <div className="bg-primary/5 rounded-xl p-4 mb-8 max-w-sm mx-auto">
                  <p className="text-sm font-medium text-foreground mb-2">Free tier includes:</p>
                  <ul className="text-sm text-muted-foreground space-y-1">
                    <li className="flex items-center gap-2">
                      <Check className="w-4 h-4 text-green-600" />
                      5 plant identifications per month
                    </li>
                    <li className="flex items-center gap-2">
                      <Check className="w-4 h-4 text-green-600" />
                      Care tips & edibility info
                    </li>
                  </ul>
                </div>

                <div className="flex flex-col sm:flex-row gap-3 justify-center">
                  <Button 
                    onClick={login}
                    className="btn-primary"
                    data-testid="login-btn"
                  >
                    <LogIn className="w-4 h-4 mr-2" />
                    Sign In with Google
                  </Button>
                  <Link to="/pricing">
                    <Button variant="outline" data-testid="view-plans-btn">
                      View Plans
                    </Button>
                  </Link>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-40 glass border-b border-border/50">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link 
              to="/saved"
              className="text-muted-foreground hover:text-primary transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
            </Link>
            <div>
              <h1 className="font-serif text-xl font-bold text-foreground">
                Plant Identifier
              </h1>
              <p className="text-sm text-muted-foreground">{getRemainingIds()}</p>
            </div>
          </div>
          <Link to="/" className="flex items-center gap-2">
            <img src="/logo.png" alt="RootSpace.Pro" className="h-8 w-auto" />
            
          </Link>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-8">
        <div className="grid md:grid-cols-2 gap-8">
          {/* Upload Section */}
          <Card className="card-organic">
            <CardHeader>
              <CardTitle className="font-serif text-xl flex items-center gap-2">
                <Camera className="w-5 h-5 text-secondary" />
                Upload Photo
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div
                className={`border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-colors ${
                  imagePreview ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/50'
                }`}
                onClick={() => fileInputRef.current?.click()}
                data-testid="upload-area"
              >
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleFileChange}
                  accept="image/*"
                  className="hidden"
                  data-testid="file-input"
                />

                {imagePreview ? (
                  <div className="space-y-4">
                    <img
                      src={imagePreview}
                      alt="Plant to identify"
                      className="max-h-64 mx-auto rounded-lg"
                    />
                    <p className="text-sm text-muted-foreground">
                      Click to change image
                    </p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <Upload className="w-12 h-12 mx-auto text-muted-foreground" />
                    <div>
                      <p className="font-medium">Click to upload</p>
                      <p className="text-sm text-muted-foreground">
                        JPG, PNG, or WEBP
                      </p>
                    </div>
                  </div>
                )}
              </div>

              <Button
                className="w-full mt-6 btn-primary"
                onClick={handleIdentify}
                disabled={!image || loading}
                data-testid="identify-btn"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Identifying...
                  </>
                ) : (
                  <>
                    <Sparkles className="w-4 h-4 mr-2" />
                    Identify Plant
                  </>
                )}
              </Button>
            </CardContent>
          </Card>

          {/* Result Section */}
          <Card className="card-organic">
            <CardHeader>
              <CardTitle className="font-serif text-xl flex items-center gap-2">
                <Leaf className="w-5 h-5 text-primary" />
                Identification Result
              </CardTitle>
            </CardHeader>
            <CardContent>
              {result ? (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="space-y-4"
                >
                  <div>
                    <h3 className="font-serif text-2xl font-bold text-foreground">
                      {result.plant_name}
                    </h3>
                    {result.scientific_name && (
                      <p className="text-sm text-muted-foreground italic">
                        {result.scientific_name}
                      </p>
                    )}
                    <Badge 
                      className={`mt-2 ${
                        result.confidence === 'high' ? 'bg-green-100 text-green-700' :
                        result.confidence === 'medium' ? 'bg-amber-100 text-amber-700' :
                        'bg-red-100 text-red-700'
                      }`}
                    >
                      {result.confidence} confidence
                    </Badge>
                  </div>

                  <p className="text-sm text-foreground leading-relaxed">
                    {result.description}
                  </p>

                  {result.is_edible !== null && (
                    <div className={`p-3 rounded-lg ${
                      result.is_edible ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'
                    }`}>
                      {result.is_edible ? "✓ This plant is edible" : "✗ Not recommended for consumption"}
                    </div>
                  )}

                  {result.care_tips?.length > 0 && (
                    <div>
                      <h4 className="font-semibold mb-2">Care Tips</h4>
                      <ul className="space-y-2">
                        {result.care_tips.map((tip, i) => (
                          <li key={i} className="flex items-start gap-2 text-sm">
                            <Check className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
                            {tip}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </motion.div>
              ) : (
                <div className="text-center py-12 text-muted-foreground">
                  <Camera className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>Upload a photo to identify a plant</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
};

export default PlantIdentifier;
